import {
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
} from '@chakra-ui/react';
import React, { ReactElement, useEffect, useMemo } from 'react';
import { Control, UseFormRegister, useForm } from 'react-hook-form';
import { useMutation } from 'urql';
import { useTranslation } from 'react-i18next';
import {
  EventResource,
  EventType,
  OrderDeepFragment,
  OrderStatus,
  ProjectDeepFragment,
  UpdateOrderDocument,
} from '../../gql/graphql';
import { orderStatusLabelMapper } from '../../hooks/order';
import {
  EventName,
  EventProperties,
  useMixpanel,
} from '../../services/mixpanel';
import OrderBadge from '../common/OrderBadge';
import StyledKeyValues from '../common/StyledKeyValues';
import UserBadge from '../common/UserBadge';
import MultilabelInput from './MultilabelInput';
import { useEventHistory } from 'src/hooks/event-history';

interface OrderInfoProps {
  editOrder: boolean;
  stopEdit: () => void;
  order: OrderDeepFragment;
  project: ProjectDeepFragment;
}

type UpdateOrderFormData = {
  name: string;
  status: OrderStatus;
  labels: { key: string; value: string }[];
};

export default function OrderInfo({
  editOrder,
  stopEdit,
  order,
  project,
}: OrderInfoProps): React.ReactElement {
  const labels: { key: string; value: string }[] = useMemo(
    () => (order.labels ? JSON.parse(order.labels) : []),
    [order.labels]
  );
  const [, updateOrder] = useMutation(UpdateOrderDocument);

  const { t } = useTranslation('project');

  const { trackEvent } = useMixpanel();

  const { register, control, handleSubmit, reset } =
    useForm<UpdateOrderFormData>({
      defaultValues: {
        name: order.name,
        status: order.status,
        labels:
          project.settings?.allowedOrderLabels?.map((projectLevelLabel) => ({
            key: projectLevelLabel.name,
            value:
              labels.find(
                (orderLabel) => orderLabel.key === projectLevelLabel.name
              )?.value ?? '',
          })) ?? [],
      },
    });

  useEffect(() => {
    reset({
      name: order.name,
      status: order.status,
      labels:
        project.settings?.allowedOrderLabels?.map((projectLevelLabel) => ({
          key: projectLevelLabel.name,
          value:
            labels.find(
              (orderLabel) => orderLabel.key === projectLevelLabel.name
            )?.value ?? '',
        })) ?? [],
    });
  }, [order, labels, reset, project.settings?.allowedOrderLabels]);

  const { logEvent } = useEventHistory(order.projectId);

  const save = handleSubmit(
    async ({ name, status, labels }: UpdateOrderFormData) => {
      const nonEmptyLabels = labels.filter(
        (label) => label.key.length > 0 && label.value.length > 0
      );
      await updateOrder({
        input: {
          id: order.id,
          patch: {
            name,
            labels: JSON.stringify(nonEmptyLabels),
            status,
          },
        },
      });
      stopEdit();

      // Track updated properties
      const updatedProperties: EventProperties = {};

      if (name !== order.name) {
        updatedProperties['Table Name'] = name;
      }

      if (status !== order.status) {
        updatedProperties['Table Status'] = status.toString();
      }

      if (JSON.stringify(nonEmptyLabels) !== order.labels) {
        updatedProperties['Labels'] = nonEmptyLabels;
      }

      trackEvent(EventName.TableInfoEdited, updatedProperties);

      logEvent({
        eventName: 'Table Updated',
        eventType: EventType.Modified,
        resourceName: order.name,
        resourceType: EventResource.Order,
        metadata: {
          affectedResources: [order.id],
          details: `Updated table ${order.name}`,
          oldName: order.name !== name ? order.name : undefined,
          newName: name !== order.name ? name : undefined,
        },
      });
    }
  );

  if (editOrder) {
    return (
      <form onSubmit={save} id="edit-order-form">
        <VStack gap={1}>
          <FormControl mt={1} isRequired>
            <FormLabel>{t('table-name')}</FormLabel>
            <Input {...register('name')} />
          </FormControl>
          {!order.isAggregatedOrder && (
            <FormControl>
              <FormLabel>{t('table-status')}</FormLabel>
              <Select {...register('status')}>
                <option value={OrderStatus.Open}>
                  {orderStatusLabelMapper(OrderStatus.Open, t)}
                </option>
                <option value={OrderStatus.InProgress}>
                  {orderStatusLabelMapper(OrderStatus.InProgress, t)}
                </option>
                <option value={OrderStatus.Completed}>
                  {orderStatusLabelMapper(OrderStatus.Completed, t)}
                </option>
              </Select>
            </FormControl>
          )}
          <MultilabelInput
            // jonasws: This was the only way to please the TypesScript gods, it seems
            control={
              control as unknown as Control<Pick<UpdateOrderFormData, 'labels'>>
            }
            register={
              register as unknown as UseFormRegister<
                Pick<UpdateOrderFormData, 'labels'>
              >
            }
          />
        </VStack>
      </form>
    );
  } else {
    let keyValues: { key: string; value: ReactElement | string }[];

    if (order.createdBy) {
      keyValues = [
        {
          key: t('table-name'),
          value: order.name,
        },
        {
          key: t('table-created-by'),
          value: <UserBadge username={order.createdBy} />,
        },
      ];
    } else {
      keyValues = [
        {
          key: t('table-name'),
          value: order.name,
        },
      ];
    }

    if (!order.isAggregatedOrder) {
      keyValues.push({
        key: t('table-status'),
        value: <OrderBadge orderStatus={order.status} />,
      });
    }

    return <StyledKeyValues keyValues={keyValues.concat(labels)} />;
  }
}
